import React, { useState, useEffect } from 'react';
import logo from './assets/logo/Track my day logo.png';
import { Amplify } from 'aws-amplify';
import { signOut, getCurrentUser, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import awsConfig from './aws-config';
import Agenda from './components/Agenda';
import Settings from './components/Settings';
import Summary from './components/Summary';
import HomeWork from './components/HomeWork';
import TravelCosts from './components/TravelCosts';
import Login from './components/Login';
import HomePage from './components/HomePage';
import Admin from './components/Admin';
import Profile from './components/Profile';
import DemoModal from './components/DemoModal';
import ReminderPopup from './components/ReminderPopup';
import YearOverview from './components/YearOverview';
import Co2Motivator from './components/Co2Motivator';
import Declaraties from './components/Declaraties';

Amplify.configure(awsConfig);

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [ingelogd, setIngelogd] = useState(false);
  const [gebruiker, setGebruiker] = useState('');
  const [userId, setUserId] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(() =>
    !Object.keys(localStorage).some(k => k.startsWith('CognitoIdentityServiceProvider'))
  );

  const controleerGroepen = async () => {
    const session = await fetchAuthSession({ forceRefresh: true });
    const groups = session.tokens?.idToken?.payload?.['cognito:groups'] || [];
    setIsAdmin(groups.includes('admins'));
  };

  const laadProfiel = async () => {
    try {
      const attrs = await fetchUserAttributes();
      setUserProfile({
        voornaam: attrs.given_name || '',
        achternaam: attrs.family_name || '',
      });
      const reminderActief = attrs['custom:monthly_reminder'] === 'true';
      const alDismissed = sessionStorage.getItem('reminderPopupDismissed') === '1';
      if (!reminderActief && !alDismissed) {
        setShowReminderPopup(true);
      }
    } catch {}
  };

  useEffect(() => {
    getCurrentUser()
      .then(async (user) => {
        setIngelogd(true);
        setGebruiker(user.signInDetails?.loginId || user.username || '');
        setUserId(user.userId || '');
        await controleerGroepen();
        await laadProfiel();
      })
      .catch(() => setIngelogd(false))
      .finally(() => setAuthChecked(true));
  }, []);

  const userPrefix = userId ? `${userId}_` : '';
  const [currentView, setCurrentView] = useState(() => localStorage.getItem('currentView') || 'settings');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [userSettings, setUserSettings] = useState({
    kmPerRitFiets: '',
    kmPerRitAuto: '',
    vergoedingPerKm: 0.23,
    vergoedingPerKmZakelijk: 0.23,
    thuiswerkVergoeding: 2.45,
    defaultThuiswerkDagen: [],
    werkdagen: [1, 2, 3, 4, 5],
    heeftNsBusinesscard: true
  });
  const [registrations, setRegistrations] = useState({});
  const [zakelijkeReizen, setZakelijkeReizen] = useState({});
  const [ovBedragen, setOvBedragen] = useState({});
  const [manualTrips, setManualTrips] = useState({});
  const [customDistances, setCustomDistances] = useState({});
  const [remarks, setRemarks] = useState({});
  const [afasSubmissions, setAfasSubmissions] = useState({});
  const [ovBonnetjes, setOvBonnetjes] = useState({});
  const [agendaFilters, setAgendaFilters] = useState({ verbergWeekenden: false, verbergFeestdagen: false, verbergVrij: false });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [userProfile, setUserProfile] = useState({ voornaam: '', achternaam: '' });
  const [demoOpen, setDemoOpen] = useState(false);
  const [showReminderPopup, setShowReminderPopup] = useState(false);

  // Load data from localStorage on component mount (or when user changes)
  useEffect(() => {
    if (!ingelogd) return;
    const savedSettings = localStorage.getItem(`${userPrefix}userSettings`);
    const savedRegistrations = localStorage.getItem(`${userPrefix}registrations`);
    const savedZakelijkeReizen = localStorage.getItem(`${userPrefix}zakelijkeReizen`);
    const savedManualTrips = localStorage.getItem(`${userPrefix}manualTrips`);
    const savedCustomDistances = localStorage.getItem(`${userPrefix}customDistances`);
    const savedRemarks = localStorage.getItem(`${userPrefix}remarks`);
    const savedOvBonnetjes = localStorage.getItem(`${userPrefix}ovBonnetjes`);
    if (savedOvBonnetjes) setOvBonnetjes(JSON.parse(savedOvBonnetjes));
    const savedAfasSubmissions = localStorage.getItem(`${userPrefix}afasSubmissions`);
    const savedMonth = localStorage.getItem(`${userPrefix}currentMonth`);
    const savedYear = localStorage.getItem(`${userPrefix}currentYear`);

    const normalizeNumber = (value) => {
      if (typeof value === 'string') {
        const normalized = value.replace(',', '.');
        const parsed = parseFloat(normalized);
        return Number.isNaN(parsed) ? undefined : parsed;
      }
      return typeof value === 'number' ? value : undefined;
    };

    if (savedSettings) {
      const parsedSettings = JSON.parse(savedSettings);
      if (parsedSettings.kmPerRitFiets === undefined && parsedSettings.kmPerRit !== undefined) {
        parsedSettings.kmPerRitFiets = normalizeNumber(parsedSettings.kmPerRit);
        parsedSettings.kmPerRitAuto = normalizeNumber(parsedSettings.kmPerRit);
      }
      parsedSettings.kmPerRitFiets = normalizeNumber(parsedSettings.kmPerRitFiets) ?? parsedSettings.kmPerRitFiets;
      parsedSettings.kmPerRitAuto = normalizeNumber(parsedSettings.kmPerRitAuto) ?? parsedSettings.kmPerRitAuto;
      parsedSettings.vergoedingPerKm = normalizeNumber(parsedSettings.vergoedingPerKm) ?? parsedSettings.vergoedingPerKm;
      if (parsedSettings.vergoedingPerKm === 0.21) parsedSettings.vergoedingPerKm = 0.23;
      parsedSettings.thuiswerkVergoeding = normalizeNumber(parsedSettings.thuiswerkVergoeding) ?? parsedSettings.thuiswerkVergoeding;
      parsedSettings.vergoedingPerKmZakelijk = normalizeNumber(parsedSettings.vergoedingPerKmZakelijk) ?? 0.23;
      setUserSettings(parsedSettings);
    }
    if (savedRegistrations) {
      setRegistrations(JSON.parse(savedRegistrations));
    }
    if (savedZakelijkeReizen) {
      setZakelijkeReizen(JSON.parse(savedZakelijkeReizen));
    }
    if (savedManualTrips) {
      setManualTrips(JSON.parse(savedManualTrips));
    }
    if (savedCustomDistances) {
      setCustomDistances(JSON.parse(savedCustomDistances));
    }
    if (savedRemarks) {
      setRemarks(JSON.parse(savedRemarks));
    }
    if (savedAfasSubmissions) {
      setAfasSubmissions(JSON.parse(savedAfasSubmissions));
    }
    const savedAgendaFilters = localStorage.getItem(`${userPrefix}agendaFilters`);
    if (savedAgendaFilters) {
      setAgendaFilters(JSON.parse(savedAgendaFilters));
    }
    const savedOvBedragen = localStorage.getItem(`${userPrefix}ovBedragen`);
    if (savedOvBedragen) setOvBedragen(JSON.parse(savedOvBedragen));
    const savedProfilePhoto = localStorage.getItem(`${userPrefix}profilePhoto`);
    if (savedProfilePhoto) setProfilePhoto(savedProfilePhoto);
    if (savedMonth !== null) {
      setCurrentMonth(parseInt(savedMonth, 10));
    }
    if (savedYear !== null) {
      setCurrentYear(parseInt(savedYear, 10));
    }
  }, [ingelogd, userPrefix]);

  // Save data to localStorage (per gebruiker)
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}userSettings`, JSON.stringify(userSettings)); }, [userSettings, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}registrations`, JSON.stringify(registrations)); }, [registrations, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}zakelijkeReizen`, JSON.stringify(zakelijkeReizen)); }, [zakelijkeReizen, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}manualTrips`, JSON.stringify(manualTrips)); }, [manualTrips, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}customDistances`, JSON.stringify(customDistances)); }, [customDistances, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}remarks`, JSON.stringify(remarks)); }, [remarks, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}afasSubmissions`, JSON.stringify(afasSubmissions)); }, [afasSubmissions, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}ovBonnetjes`, JSON.stringify(ovBonnetjes)); }, [ovBonnetjes, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}agendaFilters`, JSON.stringify(agendaFilters)); }, [agendaFilters, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}ovBedragen`, JSON.stringify(ovBedragen)); }, [ovBedragen, userPrefix, ingelogd]);
  useEffect(() => {
    if (!ingelogd) return;
    if (profilePhoto) localStorage.setItem(`${userPrefix}profilePhoto`, profilePhoto);
    else localStorage.removeItem(`${userPrefix}profilePhoto`);
  }, [profilePhoto, userPrefix, ingelogd]);


  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}currentMonth`, currentMonth.toString()); }, [currentMonth, userPrefix, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem('currentView', currentView); }, [currentView, ingelogd]);
  useEffect(() => { if (ingelogd) localStorage.setItem(`${userPrefix}currentYear`, currentYear.toString()); }, [currentYear, userPrefix, ingelogd]);

  const rapportageViews =['summary', 'homework', 'woonwerkkosten', 'zakelijkereizen', 'jaaroverzicht'];
  const overzichtItems = [
    { id: 'summary', label: 'Samenvatting' },
    { id: 'homework', label: 'Thuiswerkdagen' },
    { id: 'woonwerkkosten', label: 'Woon-werk-verkeer' },
    { id: 'zakelijkereizen', label: 'Zakelijke reizen' },
    { id: 'jaaroverzicht', label: 'Jaaroverzicht' },
  ];

  const navigation = [
    { id: 'settings', label: 'Instellingen' },
    { id: 'agenda', label: 'Registratie', primary: true },
    { id: 'overzicht', label: 'Overzicht' },
    { id: 'declaraties', label: 'Declaraties' },
    ...(isAdmin ? [{ id: 'admin', label: 'Gebruikersbeheer' }] : []),
    { id: 'profiel', label: 'Profiel' },
  ];

  const goToPreviousMonth = () => {
    const previous = new Date(currentYear, currentMonth - 1);
    setCurrentMonth(previous.getMonth());
    setCurrentYear(previous.getFullYear());
  };

  const goToNextMonth = () => {
    const next = new Date(currentYear, currentMonth + 1);
    setCurrentMonth(next.getMonth());
    setCurrentYear(next.getFullYear());
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'settings':
        return <Settings userSettings={userSettings} setUserSettings={setUserSettings} />;
      case 'agenda':
        return (
          <Agenda
            month={currentMonth}
            year={currentYear}
            registrations={registrations}
            setRegistrations={setRegistrations}
            customDistances={customDistances}
            setCustomDistances={setCustomDistances}
            remarks={remarks}
            setRemarks={setRemarks}
            userSettings={userSettings}
            zakelijkeReizen={zakelijkeReizen}
            setZakelijkeReizen={setZakelijkeReizen}
            agendaFilters={agendaFilters}
            setAgendaFilters={setAgendaFilters}
            ovBedragen={ovBedragen}
            setOvBedragen={setOvBedragen}
            ovBonnetjes={ovBonnetjes}
            setOvBonnetjes={setOvBonnetjes}
            userId={userId}
          />
        );
      case 'summary':
        return (
          <Summary
            month={currentMonth}
            year={currentYear}
            registrations={registrations}
            userSettings={userSettings}
          />
        );
      case 'homework':
        return (
          <HomeWork
            month={currentMonth}
            year={currentYear}
            registrations={registrations}
            userSettings={userSettings}
            userProfile={userProfile}
            afasSubmissions={afasSubmissions}
            setAfasSubmissions={setAfasSubmissions}
          />
        );
      case 'woonwerkkosten':
        return (
          <TravelCosts
            month={currentMonth}
            year={currentYear}
            registrations={registrations}
            manualTrips={manualTrips}
            setManualTrips={setManualTrips}
            customDistances={customDistances}
            zakelijkeReizen={zakelijkeReizen}
            userSettings={userSettings}
            userProfile={userProfile}
            afasSubmissions={afasSubmissions}
            setAfasSubmissions={setAfasSubmissions}
            ovBedragen={ovBedragen}
            section="woonwerk"
          />
        );
      case 'zakelijkereizen':
        return (
          <TravelCosts
            month={currentMonth}
            year={currentYear}
            registrations={registrations}
            manualTrips={manualTrips}
            setManualTrips={setManualTrips}
            customDistances={customDistances}
            zakelijkeReizen={zakelijkeReizen}
            userSettings={userSettings}
            userProfile={userProfile}
            afasSubmissions={afasSubmissions}
            setAfasSubmissions={setAfasSubmissions}
            ovBedragen={ovBedragen}
            section="zakelijk"
          />
        );
      case 'jaaroverzicht':
        return (
          <YearOverview
            year={currentYear}
            registrations={registrations}
            userSettings={userSettings}
          />
        );
      case 'declaraties':
        return (
          <Declaraties
            month={currentMonth}
            year={currentYear}
            userId={userId}
            userProfile={userProfile}
            ovBedragen={ovBedragen}
            ovBonnetjes={ovBonnetjes}
          />
        );
      case 'admin':
        return <Admin />;
      case 'profiel':
        return <Profile profilePhoto={profilePhoto} setProfilePhoto={setProfilePhoto} />;
      default:
        return (
          <Agenda
            month={currentMonth}
            year={currentYear}
            registrations={registrations}
            setRegistrations={setRegistrations}
            customDistances={customDistances}
            setCustomDistances={setCustomDistances}
            remarks={remarks}
            setRemarks={setRemarks}
            userSettings={userSettings}
            zakelijkeReizen={zakelijkeReizen}
            setZakelijkeReizen={setZakelijkeReizen}
            agendaFilters={agendaFilters}
            setAgendaFilters={setAgendaFilters}
            ovBedragen={ovBedragen}
            setOvBedragen={setOvBedragen}
            ovBonnetjes={ovBonnetjes}
            setOvBonnetjes={setOvBonnetjes}
            userId={userId}
          />
        );
    }
  };

  const handleLogout = async () => {
    await signOut();
    localStorage.removeItem('currentView');
    setIngelogd(false);
  };

  if (!authChecked) {
    return <div className="min-h-screen bg-gray-50" />;
  }

  if (!ingelogd) {
    const handleLogin = async (loginInput) => {
      const currentUser = await getCurrentUser();
      setIngelogd(true);
      setGebruiker(loginInput);
      setUserId(currentUser.userId || '');
      setCurrentView('settings');
      await controleerGroepen();
      await laadProfiel();
      navigate('/');
    };

    if (location.pathname === '/login') {
      return <Login initialScherm="login" onTerug={() => navigate('/')} onLogin={handleLogin} />;
    }
    if (location.pathname === '/register') {
      return <Login initialScherm="registreer" onTerug={() => navigate('/')} onLogin={handleLogin} />;
    }
    return (
      <>
        <HomePage
          onInloggen={() => navigate('/login')}
          onRegistreren={() => navigate('/register')}
          onDemo={() => navigate('/demo')}
          demoOpen={location.pathname === '/demo'}
          onDemoClose={() => navigate('/')}
        />
      </>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
      <header className="flex-shrink-0 text-white shadow-lg" style={{ backgroundColor: '#172554' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 flex justify-between items-center">
            <img src={logo} alt="Track My Day" className="h-32 w-auto flex-shrink-0 bg-white rounded-xl px-4 py-2" />
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentView('profiel')}
                className="flex items-center space-x-2 hover:opacity-80 transition ml-2"
                title="Mijn profiel"
              >
                <div className="w-8 h-8 rounded-full overflow-hidden bg-blue-500 flex items-center justify-center flex-shrink-0 ring-2 ring-blue-300">
                  {profilePhoto
                    ? <img src={profilePhoto} alt="Profiel" className="w-full h-full object-cover" />
                    : <span className="text-white text-sm font-bold">{gebruiker ? gebruiker[0].toUpperCase() : '?'}</span>
                  }
                </div>
                <span className="text-blue-200 text-sm">{gebruiker}</span>
              </button>
              <button
                onClick={handleLogout}
                className="rounded-lg bg-white/10 hover:bg-white/20 px-3 py-2 text-sm font-medium transition ml-2"
              >
                Uitloggen
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-shrink-0 bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-inner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center space-x-3 py-3">
            <button
              onClick={goToPreviousMonth}
              className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 font-medium transition"
              aria-label="Vorige maand"
            >
              ←
            </button>
            <select
              value={currentMonth}
              onChange={(e) => setCurrentMonth(parseInt(e.target.value, 10))}
              className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-base font-semibold cursor-pointer transition text-white"
            >
              {Array.from({ length: 12 }, (_, i) => {
                const monthKey = `${currentYear}-${String(i + 1).padStart(2, '0')}`;
                const sub = afasSubmissions[monthKey] || {};
                const aantalIngediend = [sub.thuiswerk, sub.reiskosten, sub.zakelijkeReizen].filter(Boolean).length;
                const vinkje = aantalIngediend >= 3 ? ' ✓✓✓' : aantalIngediend === 2 ? ' ✓✓' : aantalIngediend === 1 ? ' ✓' : '';
                return (
                  <option key={i} value={i}>
                    {new Date(2024, i).toLocaleDateString('nl-NL', { month: 'long' })}{vinkje}
                  </option>
                );
              })}
            </select>
            <select
              value={currentYear}
              onChange={(e) => setCurrentYear(parseInt(e.target.value, 10))}
              className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-base font-semibold cursor-pointer transition text-white"
            >
              {Array.from({ length: 7 }, (_, i) => 2024 + i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              onClick={goToNextMonth}
              className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 font-medium transition"
              aria-label="Volgende maand"
            >
              →
            </button>
          </div>
        </div>
      </div>

      {demoOpen && <DemoModal onClose={() => setDemoOpen(false)} />}
      {showReminderPopup && (
        <ReminderPopup
          voornaam={userProfile.voornaam}
          onClose={() => setShowReminderPopup(false)}
        />
      )}

      <nav className="flex-shrink-0 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'overzicht') {
                    if (!rapportageViews.includes(currentView)) setCurrentView('summary');
                  } else {
                    setCurrentView(item.id);
                  }
                }}
                className={`py-4 px-4 font-medium text-sm rounded-t-lg transition ${
                  item.id === 'overzicht'
                    ? rapportageViews.includes(currentView)
                      ? 'bg-blue-700 text-white'
                      : 'text-gray-600 hover:text-blue-700 hover:bg-gray-100'
                    : item.primary
                      ? currentView === item.id
                        ? 'bg-orange-500 text-white'
                        : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 font-semibold'
                      : currentView === item.id
                        ? 'bg-blue-700 text-white'
                        : 'text-gray-600 hover:text-blue-700 hover:bg-gray-100'
                }`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => setDemoOpen(true)}
              className="py-4 px-4 font-medium text-sm rounded-t-lg transition text-gray-600 hover:text-blue-700 hover:bg-gray-100 ml-auto flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Demo
            </button>
          </div>
        </div>
      </nav>

      <Co2Motivator year={currentYear} registrations={registrations} userSettings={userSettings} />

      {rapportageViews.includes(currentView) && (
        <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-1 overflow-x-auto">
              {overzichtItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setCurrentView(item.id)}
                  className={`py-2.5 px-5 text-sm font-medium whitespace-nowrap transition border-b-2 -mb-px ${
                    currentView === item.id
                      ? 'border-blue-600 text-blue-700 bg-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col pt-8 pb-2">
          <div className="flex-1 min-h-0 overflow-y-auto">
            {renderCurrentView()}
          </div>
          <footer className="flex-shrink-0 py-3 text-center text-xs text-gray-400">
            © 2026 Powered by <a href="https://www.novarockets.nl" target="_blank" rel="noopener noreferrer" className="hover:text-gray-200 underline">Nova Rockets</a>
          </footer>
        </div>
      </main>
    </div>
  );
}

export default App;